import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Multi-condition price alert system.
 * Each alert has one or more conditions combined with AND/OR.
 * Condition types: price >/</>=/<= value, indicator crosses value
 */

function evalAlertCondition(cond, currentPrice, prevPrice, indicators) {
  const { type, source, operator, value } = cond;

  let current, prev;
  if (type === 'price') {
    current = currentPrice;
    prev = prevPrice;
  } else if (type === 'indicator' && indicators[source]) {
    const vals = indicators[source];
    current = vals[vals.length - 1];
    prev = vals.length > 1 ? vals[vals.length - 2] : current;
  } else {
    return false;
  }

  if (current == null) return false;
  const v = parseFloat(value);

  switch (operator) {
    case '>': return current > v;
    case '<': return current < v;
    case '>=': return current >= v;
    case '<=': return current <= v;
    case 'crossover': return prev != null && prev <= v && current > v;
    case 'crossunder': return prev != null && prev >= v && current < v;
    default: return false;
  }
}

export default function usePriceAlerts(currentPrice, indicatorValues = {}) {
  const [alerts, setAlerts] = useState([]);
  const [triggered, setTriggered] = useState([]);
  const prevPriceRef = useRef(null);
  const nextId = useRef(1);

  // Simple alert (backwards compat)
  const addAlert = useCallback((price) => {
    if (!price || price <= 0) return;
    setAlerts(prev => [...prev, {
      id: nextId.current++,
      active: true,
      logic: 'AND',
      conditions: [{ type: 'price', source: 'price', operator: 'crossover', value: String(price) }],
      label: `Price @ ${price.toFixed(2)}`,
    }]);
  }, []);

  // Multi-condition alert
  const addMultiAlert = useCallback((label, conditions, logic = 'AND') => {
    setAlerts(prev => [...prev, {
      id: nextId.current++,
      active: true,
      logic,
      conditions,
      label: label || 'Custom Alert',
    }]);
  }, []);

  const removeAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
  }, []);

  const clearTriggered = useCallback(() => setTriggered([]), []);

  useEffect(() => {
    if (!currentPrice || prevPriceRef.current == null) {
      prevPriceRef.current = currentPrice;
      return;
    }
    const prev = prevPriceRef.current;
    prevPriceRef.current = currentPrice;

    setAlerts(current => {
      let changed = false;
      const updated = current.map(a => {
        if (!a.active || !a.conditions || a.conditions.length === 0) return a;

        const results = a.conditions.map(c => evalAlertCondition(c, currentPrice, prev, indicatorValues));
        const fired = a.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);

        if (fired) {
          changed = true;
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain); gain.connect(ctx.destination);
            osc.frequency.value = 880; osc.type = 'sine'; gain.gain.value = 0.15;
            osc.start();
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
            osc.stop(ctx.currentTime + 0.3);
          } catch {}

          setTriggered(t => [...t, {
            id: a.id, label: a.label,
            time: new Date().toLocaleTimeString(),
          }]);
          return { ...a, active: false };
        }
        return a;
      });
      return changed ? updated : current;
    });
  }, [currentPrice, indicatorValues]);

  return { alerts, triggered, addAlert, addMultiAlert, removeAlert, clearTriggered };
}
